import React from "react";
import { useTranslation } from "react-i18next";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface Option {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface AppMultiSelectCommandProps {
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  options: Option[];
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  defaultOpen?: boolean;
  colorScheme?: 'blue' | 'purple' | 'green' | 'amber' | 'default';
}

export const AppMultiSelectCommand: React.FC<AppMultiSelectCommandProps> = ({
  value = [],
  onChange,
  options,
  label,
  colorScheme = 'default'
}) => {
  const { t } = useTranslation();
  const selectedOptions = options.filter((option) => value.includes(option.value));

  // Color scheme styles
  const getColorStyles = () => {
    switch (colorScheme) {
      case 'blue':
        return {
          selected: "bg-blue-50 text-blue-700",
          icon: "text-blue-500"
        };
      case 'purple':
        return {
          selected: "bg-purple-50 text-purple-700",
          icon: "text-purple-500"
        };
      case 'green':
        return {
          selected: "bg-green-50 text-green-700",
          icon: "text-green-500"
        };
      case 'amber':
        return {
          selected: "bg-amber-50 text-amber-700",
          icon: "text-amber-500"
        };
      default:
        return {
          selected: "bg-accent",
          icon: ""
        };
    }
  };

  const colorStyles = getColorStyles();

  return (
    <Command>
      <CommandInput placeholder={t("common.search")} />
      <CommandList>
        <CommandEmpty>{t("dataTable.noResults")}</CommandEmpty>
        {value.length > 0 && (
          <CommandGroup heading={t("common.selected")}>
            {selectedOptions.map((option) => (
              <CommandItem
                key={option.value.toString()}
                value={option.label}
                onSelect={() => {
                  onChange(value.filter((v) => v !== option.value));
                }}
                className={cn("cursor-pointer", colorStyles.selected)}
              >
                <Check className={cn("mr-2 h-4 w-4 opacity-100", colorScheme !== 'default' && colorStyles.icon)} />
                {option.icon && <span className="mr-2">{option.icon}</span>}
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading={label}>
          {options
            .filter((option) => !value.includes(option.value))
            .map((option) => (
              <CommandItem
                key={option.value.toString()}
                value={option.label}
                onSelect={() => {
                  onChange([...value, option.value]);
                }}
                className="cursor-pointer"
              >
                <Check className="mr-2 h-4 w-4 opacity-0" />
                {option.icon && <span className="mr-2">{option.icon}</span>}
                {option.label}
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
